import { DrmType, MediaContainer, PlaybackType, StreamingProtocol, StreamQuality } from '../../../core/player/playout-data';
import { VideoColourSpace } from '../../../core/player/video-format';
import { isRawSessionItem } from '../../../core/session-controller/session-controller';
import { checkUnsupportedValue, throwUnsupportedValue } from '../../../utils/ts';
import { ContentType } from '../../addon-playout-data';
import { ContentDescriptorEnum, LegacyVideoQuality } from './shared.enums';
export { ContentDescriptorEnum, LegacyDrmType, LegacyVideoQuality } from './shared.enums';
export function getContentDescriptor(sessionItem, playoutData) {
    var playbackType = sessionItem === null || sessionItem === void 0 ? void 0 : sessionItem.type;
    var _ref = (sessionItem === null || sessionItem === void 0 ? void 0 : sessionItem.reporting) || {}, _ref_assetMetadata = _ref.assetMetadata, assetMetadata = _ref_assetMetadata === void 0 ? {} : _ref_assetMetadata;
    var stream = playoutData === null || playoutData === void 0 ? void 0 : playoutData.stream;
    var contentType = getContentType(assetMetadata);
    switch(playbackType){
        case PlaybackType.Advert:
            break;
        case PlaybackType.Live:
            return ContentDescriptorEnum.Linear;
        case PlaybackType.SingleLiveEvent:
            return ContentDescriptorEnum.Sle;
        case PlaybackType.Preview:
            if (convertStreamProtocolType(stream) === MediaContainer.MP4) {
                return ContentDescriptorEnum.ShortForm;
            }
            return ContentDescriptorEnum.Trailer;
        case PlaybackType.FER:
            return ContentDescriptorEnum.Fer;
        case PlaybackType.VOD:
        case PlaybackType.Clip:
            if ([
                ContentType.Entertainment,
                ContentType.Kids
            ].includes(contentType)) {
                return ContentDescriptorEnum.EpisodicVod;
            } else {
                return ContentDescriptorEnum.NonEpisodicVod;
            }
        default:
            throwUnsupportedValue(playbackType, Object.values(PlaybackType));
    }
}
export function getAssetName(sessionItem, playoutData, contentDescriptor) {
    var _playoutData_assetMetadata;
    var _ref = sessionItem.reporting || {}, _ref_assetMetadata = _ref.assetMetadata, assetMetadata = _ref_assetMetadata === void 0 ? {} : _ref_assetMetadata;
    var assetId = playoutData === null || playoutData === void 0 ? void 0 : (_playoutData_assetMetadata = playoutData.assetMetadata) === null || _playoutData_assetMetadata === void 0 ? void 0 : _playoutData_assetMetadata.assetId;
    switch(contentDescriptor){
        case ContentDescriptorEnum.Linear:
            {
                var liveMetadata = assetMetadata;
                return "LINEAR-".concat(assetId || liveMetadata.channelId);
            }
        case ContentDescriptorEnum.Sle:
            {
                var liveMetadata1 = assetMetadata;
                return "SLE-".concat(assetId || liveMetadata1.channelId);
            }
        case ContentDescriptorEnum.Fer:
            {
                var vodMetadata = assetMetadata;
                return "FER-".concat(assetId || vodMetadata.id);
            }
        case ContentDescriptorEnum.EpisodicVod:
        case ContentDescriptorEnum.NonEpisodicVod:
        case ContentDescriptorEnum.ShortForm:
            {
                var vodMetadata1 = assetMetadata;
                return "VOD-".concat(assetId || vodMetadata1.id);
            }
        case ContentDescriptorEnum.Trailer:
            {
                var vodMetadata2 = assetMetadata;
                return "TRAILER-".concat(assetId || vodMetadata2.id);
            }
        default:
            {
                return assetId || ('id' in assetMetadata ? assetMetadata.id : assetMetadata.channelId);
            }
    }
}
export function getAssetGenre(sessionItem) {
    var _sessionItem_reporting_assetMetadata, _sessionItem_reporting;
    var contentType = (_sessionItem_reporting = sessionItem.reporting) === null || _sessionItem_reporting === void 0 ? void 0 : (_sessionItem_reporting_assetMetadata = _sessionItem_reporting.assetMetadata) === null || _sessionItem_reporting_assetMetadata === void 0 ? void 0 : _sessionItem_reporting_assetMetadata.genre;
    if (typeof contentType === 'string') {
        return contentType.toLowerCase();
    }
    return typeof contentType === 'number' ? ContentType[contentType].toLowerCase() : '';
}
export function getAssetEncodingTag(stream) {
    var adaptedEncoding = adaptEncoding(stream);
    if (!adaptedEncoding) {
        return '';
    }
    return buildAssetEncodingTag(stream, adaptedEncoding);
}
export function didResumeFromBookmark(playoutData) {
    var isVod = (playoutData === null || playoutData === void 0 ? void 0 : playoutData.type) === PlaybackType.VOD;
    var isFER = (playoutData === null || playoutData === void 0 ? void 0 : playoutData.type) === PlaybackType.FER;
    return "".concat((isVod || isFER) && !!(playoutData === null || playoutData === void 0 ? void 0 : playoutData.position));
}
export function getVideoExperience(sessionItem) {
    var _sessionItem_reporting;
    return ((_sessionItem_reporting = sessionItem.reporting) === null || _sessionItem_reporting === void 0 ? void 0 : _sessionItem_reporting.contentPlaybackSource) || '';
}
export function getAdBreakSequence(availableAdBreaks, id) {
    var getSequence = -1;
    var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
    try {
        for(var _iterator = availableAdBreaks[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
            var adBreak = _step.value;
            getSequence = adBreak.ads.findIndex(function(adBreakAd) {
                return adBreakAd.id === id;
            });
            if (getSequence !== -1) {
                break;
            }
        }
    } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
    } finally{
        try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
                _iterator.return();
            }
        } finally{
            if (_didIteratorError) {
                throw _iteratorError;
            }
        }
    }
    return getSequence < 0 ? 'NA' : String(getSequence + 1);
}
function getContentType(assetMetadata) {
    var contentType;
    if (assetMetadata.contentType) {
        contentType = assetMetadata.contentType;
    } else if (assetMetadata.seriesTitle) {
        contentType = ContentType.Entertainment;
    }
    return contentType;
}
function convertStreamProtocolType(stream) {
    if (!stream || !stream.protocol) {
        return null;
    }
    switch(stream.protocol){
        case StreamingProtocol.DASH:
        case StreamingProtocol.HLS:
        case StreamingProtocol.MSS:
        case StreamingProtocol.PSF:
            return stream.protocol;
        case StreamingProtocol.PDL:
            return stream.container === MediaContainer.MP4 ? MediaContainer.MP4 : null;
        default:
            throwUnsupportedValue(stream.protocol, Object.values(StreamingProtocol));
    }
}
function adaptEncoding(stream) {
    if (!stream) {
        return null;
    }
    return {
        audioCodec: stream.audioCodec,
        videoCodec: stream.videoCodec,
        videoContainer: stream.container,
        videoQuality: adaptEncodingVideoQuality(stream.quality)
    };
}
function adaptEncodingVideoQuality(streamQuality) {
    switch(streamQuality){
        case StreamQuality.HD:
            return LegacyVideoQuality.Hd;
        case StreamQuality.SD:
            return LegacyVideoQuality.Sd;
        case StreamQuality.UHD:
            return LegacyVideoQuality.Uhd;
        default:
            checkUnsupportedValue(streamQuality);
            return streamQuality;
    }
}
function buildAssetEncodingTag(stream, encoding) {
    var streamingProtocol = convertStreamProtocol(stream);
    var drmType = stream.protection ? convertDrmType(stream.protection) : 'NONE';
    var videoQuality = convertVideoQuality(encoding === null || encoding === void 0 ? void 0 : encoding.videoQuality);
    var colourSpace = convertColourSpace(stream.colourSpace);
    var audioCodecTag = (encoding === null || encoding === void 0 ? void 0 : encoding.audioCodec) || 'UNKNOWN';
    var videoCodecTag = (encoding === null || encoding === void 0 ? void 0 : encoding.videoCodec) || 'UNKNOWN';
    var videoContainer = (encoding === null || encoding === void 0 ? void 0 : encoding.videoContainer) || 'UNKNOWN';
    return "".concat(drmType, "|").concat(streamingProtocol, "|").concat(audioCodecTag, "|").concat(videoCodecTag, "|").concat(videoContainer, "|").concat(videoQuality, "|").concat(colourSpace);
}
function convertColourSpace(colourSpace) {
    return colourSpace !== undefined ? String(Object.values(VideoColourSpace)[colourSpace]) : 'UNKNOWN';
}
function convertStreamProtocol(stream) {
    var container = stream.container, protocol = stream.protocol;
    switch(protocol){
        case StreamingProtocol.MSS:
            return 'HSS';
        case StreamingProtocol.HLS:
            return 'HLS';
        case StreamingProtocol.DASH:
            return 'DASH';
        case StreamingProtocol.PDL:
            return container === MediaContainer.MP4 ? 'MP4' : 'UNKNOWN';
        default:
            return 'UNKNOWN';
    }
}
function convertDrmType(drmType) {
    switch(drmType){
        case DrmType.PlayReady:
            return 'PLAYREADY';
        case DrmType.Widevine:
            return 'WIDEVINE';
        case DrmType.Marlin:
            return 'MS3';
        case DrmType.Fairplay:
            return 'FAIRPLAY';
        default:
            return 'UNKNOWN';
    }
}
function convertVideoQuality(quality) {
    switch(quality){
        case LegacyVideoQuality.Hd:
            return 'HD';
        case LegacyVideoQuality.Sd:
            return 'SD';
        case LegacyVideoQuality.Uhd:
            return 'UHD';
        default:
            return 'UNKNOWN';
    }
}
export function getDuration(seekableRange) {
    return seekableRange.end;
}
export function getAssetId(sessionItem, playoutData) {
    var _playoutData_advertising, _playoutData_assetMetadata;
    var assetId;
    var contentId = (_playoutData_advertising = playoutData.advertising) === null || _playoutData_advertising === void 0 ? void 0 : _playoutData_advertising.contentId;
    var assetMetadataId = (_playoutData_assetMetadata = playoutData.assetMetadata) === null || _playoutData_assetMetadata === void 0 ? void 0 : _playoutData_assetMetadata.assetId;
    if (playoutData.type !== PlaybackType.Advert && (contentId || assetMetadataId)) {
        assetId = contentId !== null && contentId !== void 0 ? contentId : assetMetadataId;
    } else if (isRawSessionItem(sessionItem)) {
        assetId = sessionItem.manifests[0];
    } else {
        assetId = sessionItem.key;
    }
    return assetId;
}

//# sourceMappingURL=asset-info.js.map